import {
    Drawer,
    DrawerContent,
    DrawerHeader,
    DrawerBody,
    DrawerFooter
} from "@heroui/drawer";
import { Button } from "@heroui/button";
import { Input } from "@heroui/input";
import { Spinner } from "@heroui/spinner";
import { Avatar } from "@heroui/avatar";
import { useState, useEffect, useRef, useCallback } from "react";
import { useChatStore } from "../_store/useChatStore";
import axios from "axios";
import Swal from "sweetalert2";
import { getModuleUrl } from '@module:shared/_utils/modules';
import { IoSend, IoImage, IoClose, IoCamera } from "react-icons/io5";
import { LuCheck, LuCheckCheck } from "react-icons/lu";

/**
 * Fungsi untuk menginisiasi dan membuka chat dari komponen lain.
 * Panggil fungsi ini pada onPress tombol Chat.
 */
export const openChat = async (donationId, donorId, courierId, currentUserId) => {
    const chatStore = useChatStore.getState();
    
    if (currentUserId) {
        chatStore.setCurrentUserId(currentUserId);
    }

    chatStore.setIsOpen(true);
    
    try {
        const url = getModuleUrl('retoys', '/chat/initiate');
        const response = await axios.post(url, {
            donation_id: donationId,
            donor_id: donorId,
            courier_id: courierId
        });
        
        const { conversation_id, messages, donor, courier } = response.data;
        
        chatStore.setConversationId(conversation_id);
        chatStore.setMessages(messages || []);

        if (currentUserId && donor && courier) {
            const partner = donor.id === currentUserId ? courier : donor;
            chatStore.setPartner(partner);
        }

        // Mark messages from partner as read
        if (conversation_id && messages?.some(m => m.sender_id !== currentUserId && !m.read_at)) {
            try {
                const readUrl = getModuleUrl('retoys', '/chat/read');
                const readResponse = await axios.post(readUrl, {
                    conversation_id,
                });
                chatStore.markMessagesAsRead(readResponse.data.read_at);
            } catch (err) {
                console.error("Gagal mark as read:", err);
            }
        }
    } catch (error) {
        console.error("Gagal menginisiasi chat:", error);
    }
};

/**
 * Icon status pengiriman pesan.
 */
function MessageStatus({ message, isSender }) {
    if (!isSender) return null;

    if (message.is_optimistic) {
        return <LuCheck className="inline-block text-[11px] text-white/40 ml-1" />;
    }

    if (message.read_at) {
        return <LuCheckCheck className="inline-block text-[11px] text-blue-600 ml-1" />;
    }

    return <LuCheck className="inline-block text-[11px] text-white/60 ml-1" />;
}

/**
 * Komponen bubble pesan tunggal.
 */
function ChatBubble({ message, isSender, onImageClick }) {
    const senderName = message?.sender?.fullname ?? 'User';
    const initials = senderName
        .split(' ')
        .map((w) => w[0])
        .slice(0, 2)
        .join('')
        .toUpperCase();

    const timestamp = message.created_at
        ? new Date(message.created_at).toLocaleTimeString('id-ID', {
              hour: '2-digit',
              minute: '2-digit',
          })
        : '';

    const hasImage = message.image || message.image_preview;
    const hasText = message.content?.trim();

    return (
        <div
            className={`flex items-end gap-2 ${
                isSender ? 'flex-row-reverse' : 'flex-row'
            }`}
        >
            {!isSender && (
                <Avatar
                    name={initials}
                    size="sm"
                    classNames={{
                        base: 'bg-default-200 text-default-600 shrink-0',
                    }}
                    src={
                        message?.sender?.photo
                            ? `/storage/chat/${message.sender.photo}`
                            : undefined
                    }
                />
            )}

            <div
                className={`relative max-w-[75%] shadow-sm transition-all overflow-hidden ${
                    isSender
                        ? 'bg-green-950 text-white rounded-2xl rounded-br-none'
                        : 'bg-default-100 text-foreground rounded-2xl rounded-bl-none'
                } ${message.is_optimistic ? 'opacity-70' : ''}`}
            >
                {/* Image */}
                {hasImage && (
                    <div className="relative">
                        <img
                            src={message.image_preview || `/storage/${message.image}`}
                            alt="Lampiran"
                            className="w-full max-h-52 object-cover cursor-pointer"
                            onClick={() => {
                                const src = message.image_preview || (message.image ? `/storage/${message.image}` : null);
                                if (src) onImageClick(src);
                            }}
                        />
                        {message.is_optimistic && (
                            <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                                <Spinner size="sm" color="white" />
                            </div>
                        )}
                    </div>
                )}

                {/* Text + timestamp */}
                <div className={`${hasImage ? 'px-3 py-2' : 'px-4 py-2.5'}`}>
                    {hasText && (
                        <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                            {message.content}
                        </p>
                    )}
                    <span
                        className={`flex items-center gap-0.5 text-[10px] ${hasText ? 'mt-1' : ''} ${
                            isSender
                                ? 'text-white/60 justify-end'
                                : 'text-default-400 justify-start'
                        }`}
                    >
                        {timestamp}
                        <MessageStatus message={message} isSender={isSender} />
                    </span>
                </div>
            </div>
        </div>
    );
}

/**
 * Preview gambar yang akan dikirim.
 */
function ImagePreview({ file, onRemove }) {
    const [preview, setPreview] = useState(null);

    useEffect(() => {
        if (!file) return;
        const url = URL.createObjectURL(file);
        setPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    if (!preview) return null;

    return (
        <div className="relative inline-block mx-4 mb-2">
            <img
                src={preview}
                alt="Preview"
                className="h-20 w-20 object-cover rounded-lg border border-default-200"
            />
            <button
                type="button"
                onClick={onRemove}
                className="absolute -top-1.5 -right-1.5 bg-danger text-white rounded-full p-0.5 shadow-md hover:bg-danger-600 transition-colors"
                aria-label="Hapus gambar"
            >
                <IoClose className="text-xs" />
            </button>
        </div>
    );
}

export default function ChatDrawer() {
    const { isOpen, messages, closeChat, conversationId, addMessage, currentUserId, partner, markMessagesAsRead, markSentMessagesAsRead } = useChatStore();
    const [newMessage, setNewMessage] = useState("");
    const [imageFile, setImageFile] = useState(null);
    const [isSending, setIsSending] = useState(false);
    const [lightboxSrc, setLightboxSrc] = useState(null);
    const messagesEndRef = useRef(null);
    const bodyRef = useRef(null);
    const fileInputRef = useRef(null);
    const cameraInputRef = useRef(null);

    const handleClose = useCallback((popHistory = true) => {
        setImageFile(null);
        closeChat();
        if (popHistory && window.history.state?.chatDrawerOpen) {
            window.history.back();
        }
    }, [closeChat]);

    // --- HISTORY API / BACK BUTTON HANDLER ---
    useEffect(() => {
        const handlePopState = (e) => {
            if (isOpen) {
                // Jika tombol back ditekan saat drawer terbuka, tutup drawer
                // Jangan panggil history.back() karena navigasi back sudah terjadi
                handleClose(false); 
            }
        };

        if (isOpen) {
            window.history.pushState({ chatDrawerOpen: true }, '');
            window.addEventListener('popstate', handlePopState);
        }

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [isOpen, handleClose]);

    const scrollToBottom = useCallback((behavior = 'smooth') => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior });
        }
    }, []);

    // Echo listener untuk realtime
    useEffect(() => {
        if (!conversationId || !window.Echo) return undefined;

        const channel = window.Echo.private(`retoys.chat.${conversationId}`);

        channel.listen('.MessageSent', (e) => {
            if (e?.message) {
                addMessage(e.message);

                const readUrl = getModuleUrl('retoys', '/chat/read');
                axios.post(readUrl, { conversation_id: conversationId })
                    .catch(() => {});
            }
        });

        channel.listen('.MessagesRead', (e) => {
            if (e?.read_by && e.read_by !== currentUserId) {
                markSentMessagesAsRead(e.read_at);
            }
        });

        return () => {
            window.Echo.leave(`retoys.chat.${conversationId}`);
        };
    }, [conversationId, addMessage, currentUserId, markSentMessagesAsRead]);

    useEffect(() => {
        scrollToBottom();
    }, [messages, scrollToBottom]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isOpen) {
                if (lightboxSrc) {
                    setLightboxSrc(null);
                } else {
                    handleClose(true);
                }
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, lightboxSrc, handleClose]);

    useEffect(() => {
        if (isOpen) {
            const timer = setTimeout(() => scrollToBottom('instant'), 100);
            return () => clearTimeout(timer);
        }
    }, [isOpen, scrollToBottom]);

    const handleImageSelect = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            // Validate file type and size (max 10MB)
            const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
            if (!validTypes.includes(file.type)) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Format tidak didukung',
                    text: 'Gunakan JPEG, PNG, atau WebP.',
                    confirmButtonColor: '#14532d',
                    showClass: { popup: 'scaleIn' },
                    hideClass: { popup: 'scaleOut' },
                });
                return;
            }
            if (file.size > 10 * 1024 * 1024) {
                Swal.fire({
                    icon: 'warning',
                    title: 'File terlalu besar',
                    text: 'Ukuran gambar maksimal 10MB.',
                    confirmButtonColor: '#14532d',
                    showClass: { popup: 'scaleIn' },
                    hideClass: { popup: 'scaleOut' },
                });
                return;
            }
            setImageFile(file);
        }
        // Reset input agar bisa pilih file yang sama lagi
        e.target.value = '';
    };

    const handleSendMessage = async () => {
        const trimmedMessage = newMessage.trim();
        const hasImage = !!imageFile;

        if (!conversationId || (!trimmedMessage && !hasImage)) return;
        if (isSending) return;

        setIsSending(true);

        const optimisticId = `temp-${Date.now()}`;
        const optimisticMessage = {
            id: optimisticId,
            conversation_id: conversationId,
            sender_id: currentUserId,
            content: trimmedMessage,
            image: null,
            image_preview: hasImage ? URL.createObjectURL(imageFile) : null,
            created_at: new Date().toISOString(),
            is_optimistic: true,
            read_at: null,
        };

        addMessage(optimisticMessage);
        setNewMessage("");
        const fileToSend = imageFile;
        setImageFile(null);

        try {
            let response;

            if (hasImage) {
                // Send with image via FormData
                const formData = new FormData();
                formData.append('conversation_id', conversationId);
                formData.append('image', fileToSend);
                if (trimmedMessage) {
                    formData.append('message', trimmedMessage);
                }

                const url = getModuleUrl('retoys', '/chat/send-image');
                response = await axios.post(url, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
            } else {
                // Text-only message
                const url = getModuleUrl('retoys', '/chat/send');
                response = await axios.post(url, {
                    conversation_id: conversationId,
                    message: trimmedMessage,
                });
            }

            const savedMessage = response?.data?.message;

            if (savedMessage) {
                useChatStore.setState((state) => ({
                    messages: state.messages.map((msg) =>
                        msg.id === optimisticId ? savedMessage : msg
                    ),
                }));
            }
        } catch (error) {
            useChatStore.setState((state) => ({
                messages: state.messages.filter((msg) => msg.id !== optimisticId),
            }));
            console.error("Gagal mengirim pesan:", error);
            
            let errorMessage = "Gagal mengirim pesan. Silakan coba lagi.";
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.status === 413) {
                errorMessage = "Ukuran gambar terlalu besar (Maksimal 10MB).";
            } else if (error.response?.status === 422) {
                errorMessage = "Format gambar tidak didukung atau ukurannya terlalu besar.";
            }

            Swal.fire({
                icon: 'error',
                title: 'Gagal',
                text: errorMessage,
                confirmButtonColor: '#14532d',
                showClass: { popup: 'scaleIn' },
                hideClass: { popup: 'scaleOut' },
            });
        } finally {
            setIsSending(false);
            // Cleanup preview URL
            if (optimisticMessage.image_preview) {
                URL.revokeObjectURL(optimisticMessage.image_preview);
            }
        }
    };

    return (
        <>
            <Drawer 
                isOpen={isOpen} 
                onOpenChange={(open) => {
                    if (!open) {
                        if (lightboxSrc) {
                            setLightboxSrc(null);
                        } else {
                            handleClose(true);
                        }
                    }
                }}
                size="md"
                placement="right"
                classNames={{
                    base: "w-full !max-w-full sm:!max-w-md m-0 sm:m-auto h-full",
                    closeButton: "top-[1.1rem] right-4",
                    wrapper: "z-[1100]",
                    backdrop: "z-[1099]",
                }}
            >
            <DrawerContent>
                {(onClose) => (
                    <>
                        {/* Header */}
                        <DrawerHeader className="flex flex-row items-center gap-3 border-b border-default-200 px-5 py-4 pr-12">
                            {partner ? (
                                <Avatar
                                    name={partner.fullname.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()}
                                    size="sm"
                                    classNames={{
                                        base: 'bg-green-950 text-white shrink-0',
                                    }}
                                    src={partner.photo ? `/storage/${partner.photo}` : undefined}
                                />
                            ) : (
                                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-950 shrink-0">
                                    <IoSend className="text-white text-sm" />
                                </div>
                            )}
                            <div className="min-w-0">
                                <h3 className="text-base font-semibold leading-tight truncate">
                                    {partner?.fullname ?? 'Chat'}
                                </h3>
                            </div>
                        </DrawerHeader>

                        {/* Body */}
                        <DrawerBody
                            ref={bodyRef}
                            className="p-4 overflow-y-auto bg-default-50"
                        >
                            {!conversationId ? (
                                <div className="h-full flex flex-col items-center justify-center gap-3">
                                    <Spinner size="lg" color="success" />
                                    <p className="text-sm text-default-400 animate-pulse">
                                        Menghubungkan...
                                    </p>
                                </div>
                            ) : (
                                <div className="flex flex-col space-y-4">
                                    {messages.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center text-center mt-16 gap-2">
                                            <div className="h-12 w-12 rounded-full bg-default-100 flex items-center justify-center">
                                                <IoSend className="text-default-300 text-lg" />
                                            </div>
                                            <p className="text-default-400 text-sm">
                                                Belum ada pesan.
                                            </p>
                                            <p className="text-default-300 text-xs">
                                                Mulai percakapan sekarang!
                                            </p>
                                        </div>
                                    ) : (
                                        messages.map((msg, index) => {
                                            const isSender = msg.sender_id === currentUserId;
                                            return (
                                                <ChatBubble
                                                    key={msg.id ?? index}
                                                    message={msg}
                                                    isSender={isSender}
                                                    onImageClick={setLightboxSrc}
                                                />
                                            );
                                        })
                                    )}
                                    <div ref={messagesEndRef} />
                                </div>
                            )}
                        </DrawerBody>

                        {/* Image Preview */}
                        {imageFile && (
                            <div className="border-t border-default-100 bg-default-50">
                                <ImagePreview
                                    file={imageFile}
                                    onRemove={() => setImageFile(null)}
                                />
                            </div>
                        )}

                        {/* Footer */}
                        <DrawerFooter className="border-t border-default-200 px-4 pt-3 pb-[calc(12px+env(safe-area-inset-bottom,0px))]">
                            <div className="flex w-full items-center gap-2">
                                {/* Hidden file inputs */}
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/jpeg,image/jpg,image/png,image/webp"
                                    className="hidden"
                                    onChange={handleImageSelect}
                                />
                                <input
                                    ref={cameraInputRef}
                                    type="file"
                                    accept="image/*"
                                    capture="environment"
                                    className="hidden"
                                    onChange={handleImageSelect}
                                />

                                {/* Camera button */}
                                <Button
                                    isIconOnly
                                    variant="light"
                                    radius="full"
                                    size="md"
                                    className="text-default-500 hover:text-green-700 shrink-0"
                                    isDisabled={!conversationId || isSending}
                                    onPress={() => cameraInputRef.current?.click()}
                                    aria-label="Ambil foto"
                                >
                                    <IoCamera className="text-xl" />
                                </Button>

                                {/* Gallery button */}
                                <Button
                                    isIconOnly
                                    variant="light"
                                    radius="full"
                                    size="md"
                                    className="text-default-500 hover:text-green-700 shrink-0"
                                    isDisabled={!conversationId || isSending}
                                    onPress={() => fileInputRef.current?.click()}
                                    aria-label="Lampirkan gambar"
                                >
                                    <IoImage className="text-xl" />
                                </Button>

                                <Input 
                                    fullWidth 
                                    placeholder="Ketik pesan..." 
                                    value={newMessage}
                                    variant="bordered"
                                    radius="full"
                                    size="md"
                                    classNames={{
                                        inputWrapper: 'border-default-300 focus-within:border-primary',
                                    }}
                                    onChange={(e) => setNewMessage(e.target.value)}
                                    isDisabled={!conversationId || isSending}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            handleSendMessage();
                                        }
                                    }}
                                />
                                <Button 
                                    isIconOnly
                                    radius="full"
                                    size="md"
                                    className="bg-green-950 text-white hover:bg-green-800"
                                    isDisabled={!conversationId || (!newMessage.trim() && !imageFile) || isSending}
                                    onPress={handleSendMessage}
                                    aria-label="Kirim pesan"
                                >
                                    {isSending ? (
                                        <Spinner size="sm" color="white" />
                                    ) : (
                                        <IoSend className="text-lg" />
                                    )}
                                </Button>
                            </div>
                        </DrawerFooter>
                    </>
                )}
            </DrawerContent>
        </Drawer>

        {/* Image Lightbox - rendered outside Drawer to avoid backdrop conflicts */}
        {lightboxSrc && (
            <div
                className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-4 cursor-pointer"
                onClick={() => setLightboxSrc(null)}
                role="dialog"
                aria-label="Preview gambar"
            >
                <button
                    className="absolute top-4 right-4 text-white bg-black/50 rounded-full p-2 hover:bg-black/70 transition-colors"
                    onClick={() => setLightboxSrc(null)}
                    aria-label="Tutup preview"
                >
                    <IoClose className="text-xl" />
                </button>
                <img
                    src={lightboxSrc}
                    alt="Preview"
                    className="max-w-full max-h-full object-contain rounded-lg cursor-default"
                    onClick={(e) => e.stopPropagation()}
                />
            </div>
        )}
        </>
    );
}
